import { buildCollection, EntityReference, buildProperty, useDataSource, useEntityFetch, FieldProps } from "@firecms/core";
import { getAuth } from "firebase/auth";
import { Button, Select, SelectItem } from "@firecms/ui";
import React, { useState, useEffect } from "react";
import { config } from "../config";
import { directoryCollection } from "./directory";

/**
 * Custom Field Component to render a clean, professional Directory Reference interface.
 * Shows only the Title, ID, and a Clear button once selected.
 */
function DirectoryReferenceField({
    property,
    value,
    setValue
}: FieldProps<EntityReference>) {
    const dataSource = useDataSource();
    const [directories, setDirectories] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    // Fetch details of the selected Directory
    const { entity: selectedEntity } = useEntityFetch({
        path: "directory",
        entityId: value?.id,
        collection: directoryCollection as any,
        useCache: true
    });

    useEffect(() => {
        let active = true;
        setLoading(true);
        dataSource.fetchCollection({
            path: "directory",
            collection: directoryCollection as any,
            limit: 200,
            orderBy: "title",
            order: "asc"
        }).then((result) => {
            if (active) {
                setDirectories(result);
                setLoading(false);
            }
        }).catch((err) => {
            console.error("Error fetching directories:", err);
            if (active) setLoading(false);
        });
        return () => {
            active = false;
        };
    }, [dataSource]);

    if (value && value.id) {
        const title = selectedEntity?.values?.title || "Loading...";
        return (
            <div className="flex flex-col gap-2 p-4 border border-surface-200 dark:border-surface-800 rounded-md bg-surface-50 dark:bg-surface-900">
                <span className="text-xs font-semibold text-text-secondary dark:text-text-secondary-dark uppercase tracking-wider">
                    {property.name}
                </span>
                <div className="flex justify-between items-center gap-4">
                    <div className="flex flex-col">
                        <span className="text-sm font-medium text-text-primary dark:text-text-primary-dark">
                            {title}
                        </span>
                        <span className="text-xs font-mono text-text-secondary dark:text-text-secondary-dark mt-0.5">
                            ID: {value.id}
                        </span>
                    </div>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setValue(null)}
                    >
                        Clear
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <Select
            value="none"
            onValueChange={(val) => {
                if (val !== "none") {
                    setValue(new EntityReference(val, "directory"));
                }
            }}
            disabled={loading}
            label={property.name}
            fullWidth
            position="popper"
        >
            <SelectItem value="none"><em>Select a Directory...</em></SelectItem>
            {directories.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                    {d.values?.title || d.id}
                </SelectItem>
            ))}
        </Select>
    );
}

export const listiclePagesCollection = buildCollection({
    id: "listicle_pages",
    path: "listicle_pages",
    name: "Listicle Pages",
    properties: {
        generation_status: ({ values }: any) => {
            const isGenerating = (values as any).generating_content;
            return {
                dataType: "string" as const,
                name: "AI Generation Status",
                disabled: !isGenerating ? { hidden: true } : false,
                Field: () => (
                    <div className="flex flex-col gap-2 p-4 border border-blue-200 dark:border-blue-800 rounded-md bg-blue-50 dark:bg-blue-950/30 animate-pulse">
                        <div className="flex items-center gap-3">
                            <div className="w-5 h-5 border-2 border-blue-600 dark:border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
                                Generating listicle content via AI...
                            </span>
                        </div>
                        <span className="text-xs text-blue-600 dark:text-blue-400">
                            This can take up to a minute. Please do not close or submit this form.
                        </span>
                    </div>
                )
            };
        },
        directory_ref: buildProperty({
            dataType: "reference",
            path: "directory",
            name: "Directory Reference",
            Field: DirectoryReferenceField,
            validation: { required: true }
        }),
        resume_count: buildProperty({
            dataType: "number",
            name: "Resume Count",
            defaultValue: 10,
            validation: { required: true }
        }),
        title: ({ values }: any) => {
            const isCreationMode = typeof window !== "undefined" && window.location.hash.includes("new");
            return {
                dataType: "string",
                name: "Title",
                disabled: (isCreationMode && !values.title) ? { hidden: true } : false,
                validation: { required: !isCreationMode }
            };
        },
        intro_text: ({ values }: any) => {
            const isCreationMode = typeof window !== "undefined" && window.location.hash.includes("new");
            return {
                dataType: "string",
                name: "Intro Text",
                markdown: true,
                disabled: (isCreationMode && !values.intro_text) ? { hidden: true } : false,
                validation: { required: !isCreationMode }
            };
        },
        main_text: ({ values }: any) => {
            const isCreationMode = typeof window !== "undefined" && window.location.hash.includes("new");
            return {
                dataType: "string",
                name: "Main Text",
                markdown: true,
                disabled: (isCreationMode && !values.main_text) ? { hidden: true } : false,
                validation: { required: !isCreationMode }
            };
        },
        closing_text: ({ values }: any) => {
            const isCreationMode = typeof window !== "undefined" && window.location.hash.includes("new");
            return {
                dataType: "string",
                name: "Closing Text",
                markdown: true,
                disabled: (isCreationMode && !values.closing_text) ? { hidden: true } : false,
                validation: { required: !isCreationMode }
            };
        },
        resumes: ({ values }: any) => {
            const isCreationMode = typeof window !== "undefined" && window.location.hash.includes("new");
            const list = (values as any).resumes;
            return {
                dataType: "array",
                name: "Resumes",
                of: {
                    dataType: "reference",
                    path: "resume"
                },
                disabled: (isCreationMode && (!list || list.length === 0)) ? { hidden: true } : false,
                validation: { required: !isCreationMode }
            };
        },
        created_at: buildProperty({
            dataType: "date",
            name: "Created at",
            autoValue: "on_create"
        }),
        updated_at: buildProperty({
            dataType: "date",
            name: "Updated at",
            autoValue: "on_update"
        })
    },
    get entityActions() {
        const isCreationMode = typeof window !== "undefined" && window.location.hash.includes("new");
        if (isCreationMode) {
            return [];
        }
        return [
            {
                key: "generate_content",
                name: "Generate Content",
                icon: (
                    <span className="form-action-text-button">
                        Generate Content
                    </span>
                ),
                includeInForm: true,
                isEnabled: ({ formContext }: any) => {
                    return !formContext?.values?.generating_content;
                },
                onClick: async ({ context, formContext }: any) => {
                    if (!formContext) return;
                    const directoryRef = formContext.values.directory_ref;
                    if (!directoryRef) {
                    context.snackbarController.open({
                        type: "warning",
                        message: "Please select a Directory Reference first."
                    });
                    return;
                }

                try {
                    formContext.setFieldValue("generating_content", true);
                    context.snackbarController.open({
                        type: "info",
                        message: "Generating content..."
                    });

                    const auth = getAuth();
                    const user = auth.currentUser;
                    if (!user) {
                        throw new Error("You must be logged in to generate content.");
                    }

                    const token = await user.getIdToken();
                    const directoryId = directoryRef.id;
                    const resumeCount = formContext.values.resume_count ?? 10;

                    const response = await fetch(config.listicleGenUrl, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            directoryId,
                            resumeCount
                        })
                    });

                    if (!response.ok) {
                        const errData = await response.json().catch(() => ({}));
                        throw new Error(errData.error || `HTTP error! status: ${response.status}`);
                    }

                    const data = await response.json();

                    const { title, intro_text, main_text, closing_text, featured_resume_ids } = data || {};

                    if (title) formContext.setFieldValue("title", title);
                    if (intro_text) formContext.setFieldValue("intro_text", intro_text);
                    if (main_text) formContext.setFieldValue("main_text", main_text);
                    if (closing_text) formContext.setFieldValue("closing_text", closing_text);

                    if (Array.isArray(featured_resume_ids)) {
                        const resumeReferences = featured_resume_ids.map((id: string) => new EntityReference(id, "resume"));
                        formContext.setFieldValue("resumes", resumeReferences);
                    }

                    context.snackbarController.open({
                        type: "success",
                        message: "Content generated successfully!"
                    });
                } catch (error: any) {
                    console.error("Listicle generation failed:", error);
                    context.snackbarController.open({
                        type: "error",
                        message: `Failed to generate content: ${error.message}`
                    });
                } finally {
                    formContext.setFieldValue("generating_content", false);
                }
            }
        }
    ];
    },
    callbacks: {
        onDelete: () => {
            window.location.reload();
        }
    },
    openEntityMode: "full_screen"
});
